import { useState } from 'react';
import { useCompanions } from '../hooks/useCompanions';
import { useAuth } from '../hooks/useAuth';
import { Users, Copy, UserPlus, Trash2 } from 'lucide-react';

export function MiniMasjid() {
  const { session } = useAuth();
  const { companions, loading, addCompanion, removeCompanion } = useCompanions();
  const [inviteId, setInviteId] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const copyMyId = () => {
    if (session?.uid) {
      navigator.clipboard.writeText(session.uid);
      alert('Your Invite ID copied to clipboard!');
    }
  };

  const handleAdd = async () => {
    if (!inviteId.trim()) return;
    setAdding(true);
    setError('');
    
    const { error } = await addCompanion(inviteId.trim());
    if (error) {
        // More descriptive error for end users
        if (error.includes('duplicate key')) {
            setError('You already added this companion.');
        } else if (error.includes('not found')) {
            setError('User ID not found. Ask them to log in to Habl first.');
        } else {
            setError(error);
        }
    } else {
        setInviteId('');
    }
    setAdding(false);
  };

  const getTodayDeeds = (deeds: Record<string, number>) => {
    const today = new Date().toISOString().split('T')[0];
    return deeds?.[today] || 0;
  };

  const getGlowColor = (count: number) => {
    if (count === 0) return 'bg-slate-700';
    if (count === 1) return 'bg-emerald-900 border-emerald-800';
    if (count === 2) return 'bg-emerald-600 border-emerald-500';
    return 'bg-emerald-400 border-emerald-300 shadow-[0_0_10px_rgba(52,211,153,0.6)]';
  };

  if (!session) return null;

  return (
    <div className="mt-6 w-full max-w-md mx-auto">
      <div className="bg-slate-900 rounded-2xl p-6 border border-slate-800 text-white">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-emerald-400" />
          <h3 className="text-lg font-bold">Mini Masjid</h3>
          <span className="text-[10px] bg-emerald-900/50 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-800/50">
            Companion Mode
          </span>
        </div>

        {/* My Invite Code */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-700/50">
          <p className="text-xs text-slate-400 mb-2 uppercase tracking-wider font-bold">Your Invite ID</p>
          <div className="flex gap-2">
            <code className="flex-1 bg-slate-950 p-3 rounded-lg text-xs font-mono text-slate-300 truncate select-all">
              {session.uid}
            </code>
            <button 
              onClick={copyMyId}
              className="bg-slate-700 hover:bg-slate-600 p-3 rounded-lg transition-colors"
              title="Copy ID"
            >
              <Copy className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Add Companion */}
        <div className="mb-6">
           <div className="flex gap-2">
            <input
              type="text"
              value={inviteId}
              onChange={(e) => setInviteId(e.target.value)}
              placeholder="Paste friend's ID here..."
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-emerald-500 transition-colors"
            />
            <button 
              onClick={handleAdd}
              disabled={adding || !inviteId}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 rounded-lg transition-colors flex items-center justify-center"
            >
              {adding ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <UserPlus className="w-4 h-4" />}
            </button>
           </div>
           {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
        </div>

        {/* Companions List */}
        <div className="space-y-3">
           <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Your Circle</h4>
           
           {loading ? (
             <div className="text-center py-4 text-slate-500 text-xs">Loading circle...</div>
           ) : companions.length === 0 ? (
             <div className="text-center py-8 border-2 border-dashed border-slate-800 rounded-xl">
                <p className="text-slate-500 text-sm">No companions yet.</p>
                <p className="text-slate-600 text-xs mt-1">Share your ID to start a circle.</p>
             </div>
           ) : (
             companions.map(friend => {
               const todayCount = getTodayDeeds(friend.deeds);
               return (
                 <div key={friend.id} className="bg-slate-800 rounded-xl p-3 flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold bg-slate-700 overflow-hidden`}>
                            {friend.avatar ? (
                                <img src={friend.avatar} alt={friend.name} className="w-full h-full object-cover" />
                            ) : (
                                friend.name?.[0] || '?'
                            )}
                        </div>
                        <div>
                            <p className="font-bold text-sm text-white">{friend.name || 'Unknown'}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                                <div className={`w-2 h-2 rounded-full ${todayCount > 0 ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                                <p className="text-[10px] text-slate-400">
                                    {todayCount} deeds today
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Status Indicator */}
                        <div 
                            className={`w-3 h-3 rounded-sm border ${getGlowColor(todayCount)} transition-all duration-500`} 
                            title="Activity Level"
                        />
                        
                        <button 
                            onClick={() => removeCompanion(friend.id)}
                            className="text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                            title="Remove"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                 </div>
               );
             })
           )}
        </div>

      </div>
    </div>
  );
}
